import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as api from '../api';
import { HeaderBack, RNLLoader, Select } from '../components/UI';
import { hasRole } from '../utils';
import CGDashboard from '../components/codeGenerator/CGDashboard';
import CGGenerate from '../components/codeGenerator/CGGenerate';
import CGRegister from '../components/codeGenerator/CGRegister';
import CGApply from '../components/codeGenerator/CGApply';
import CGSettings from '../components/codeGenerator/CGSettings';

// BOQ Hub → Code Generator: BOQ codes in the MEP format PREFIX-DISC-0001 (RDV-ELECT-0001 … 9999), sub-codes …-01.
//   /boq-hub/code-generator/:tab?project=<id>      dashboard | generate | register | apply | settings
const BASE = '/boq-hub/code-generator';
const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'generate', label: 'Generate Codes' },
  { id: 'register', label: 'Code Register' },
  { id: 'apply', label: 'Apply to BOQ' },
  { id: 'settings', label: 'Settings' },
];
const WRITERS = ['project_manager', 'quantity_surveyor'];

export default function CodeGeneratorPage({ user, toast }) {
  const navigate = useNavigate();
  const { tab = 'dashboard' } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project') || '';
  const [projects, setProjects] = useState(null);
  const [settings, setSettings] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [version, setVersion] = useState(0);          // bumped after anything issues codes, so tabs reload
  const canEdit = hasRole(user, WRITERS);
  const isSuper = hasRole(user, ['super_admin']);

  useEffect(() => {
    api.listProjects().then(r => setProjects(Array.isArray(r) ? r : (r?.data || r?.items || []))).catch(() => setProjects([]));
  }, []);

  const reloadShared = useCallback(() => {
    api.clearApiCache();
    api.listCodeDisciplines().then(setDisciplines).catch(() => setDisciplines([]));
    if (projectId) api.getCodeSettings(projectId).then(setSettings).catch(() => setSettings(null));
    else setSettings(null);
    setVersion(v => v + 1);
  }, [projectId]);
  useEffect(() => { reloadShared(); }, [reloadShared]);

  const goTo = useCallback((next, query = {}) => {
    api.clearApiCache();
    const q = new URLSearchParams({ ...(projectId ? { project: projectId } : {}), ...query });
    navigate(`${BASE}/${next}${q.toString() ? `?${q}` : ''}`);
  }, [navigate, projectId]);

  const project = useMemo(() => (projects || []).find(p => String(p.project_id || p.id) === String(projectId)), [projects, projectId]);
  const shown = TABS.some(t => t.id === tab) ? tab : 'dashboard';
  if (projects === null) return <RNLLoader title="Loading Code Generator..." subtitle="Fetching projects" />;

  const shared = { projectId, project, settings, disciplines, version, onChanged: reloadShared, goTo, canEdit, isSuper, toast };
  const needsPrefix = projectId && settings && !settings.prefix && ['generate', 'apply'].includes(shown);

  return (
    <div className="fade-in" style={{ padding: '0 0 24px', marginTop: -28 }}>
      <HeaderBack onClick={() => navigate('/boq-hub')}>Back to BOQ Hub</HeaderBack>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TABS.length}, minmax(0, 1fr))`, gap: 10, marginBottom: 20, paddingTop: 16 }}>
        {TABS.map(t => {
          const on = shown === t.id;
          return (
            <button key={t.id} onClick={() => goTo(t.id)} style={{
              padding: '18px 10px', background: on ? '#0b318bff' : 'var(--bg-card)', color: on ? '#fff' : 'var(--text-primary)',
              border: '1px solid black', borderRadius: 12, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center',
              boxShadow: on ? '0 4px 12px rgba(11,49,139,0.2)' : 'var(--shadow-sm)', transition: 'all 0.2s ease',
            }}>{t.label}</button>
          );
        })}
      </div>

      <div style={{ background: 'var(--bg-card)', border: '3px solid black', borderRadius: 16, minHeight: 400, padding: 24, boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
          <div>
            <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20 }}>BOQ Code Generator</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', maxWidth: 720 }}>
              BOQ codes in the MEP format <b>{settings?.prefix || 'RDV'}-ELECT-0001</b> to <b>…-9999</b>, one running number per project and
              discipline, with sub-codes <b>…-0001-01</b> under each main code. Every code issued is kept in the register.
            </p>
          </div>
          <div style={{ width: 320, maxWidth: '100%' }}>
            <Select label="Project" value={projectId} onChange={v => { api.clearApiCache(); navigate(`${BASE}/${shown}${v ? `?project=${v}` : ''}`); }}
                    options={[{ value: '', label: '— Select a project —' },
                      ...projects.map(p => ({ value: p.project_id || p.id, label: `${p.project_code ? `${p.project_code} — ` : ''}${p.project_name}` }))]} />
          </div>
        </div>

        {!projectId && shown !== 'settings' ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>#</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>Select a project</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Codes are numbered per project — choose one above.</div>
          </div>
        ) : needsPrefix ? (
          <div style={{ padding: 16, borderRadius: 12, background: '#fef9c3', border: '1px solid #fde047', fontSize: 13.5 }}>
            Set this project's code prefix first — <button onClick={() => goTo('settings')} style={{ background: 'none', border: 'none', color: '#0b318b', fontWeight: 800, cursor: 'pointer', padding: 0 }}>open Settings</button>
            {settings?.suggested_prefix && <> (suggested: <b>{settings.suggested_prefix}</b>)</>}.
          </div>
        ) : (
          <>
            {shown === 'dashboard' && <CGDashboard {...shared} />}
            {shown === 'generate' && <CGGenerate {...shared} />}
            {shown === 'register' && <CGRegister {...shared} />}
            {shown === 'apply' && <CGApply {...shared} />}
            {shown === 'settings' && <CGSettings {...shared} />}
          </>
        )}
      </div>
    </div>
  );
}
